import React, { useEffect, useLayoutEffect } from "react";
import MainContainer from "../../components/container/MainContainer";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  addDonor,
  editDonor,
  getDonor,
  setSelectedDonor,
  unsetSelectedDonor,
} from "../../features/donorsSlice";
import { closeModal } from "../../features/modalSLice";
import AddResource from "../../components/header/AddResource";
import InputField from "../../components/form/InputField";
import SelectField from "../../components/form/SelectField";
import { getCountries, unsetCountries } from "../../features/countriesSlice";
import Loading from "../../components/loading/Loading";
import ResourseButtons from "../../components/form/buttons/ResourseButtons";
import PhoneInputField from "../../components/form/PhoneInputField";
import { useNotification } from "../../components/notification/NotificationContainer";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDonorSchema, editDonorSchema } from "../../schemas";
import { getCurrencies, unsetCurrencies } from "../../features/currenciesSlice";
import loadStatus from "../../util/loadStatus";

const AddDonor = ({ edit, id }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const donor = useSelector((state) => state.donors.selected);
  const donorLoad = useSelector((state) => state.donors.load.getDonor);
  const countries = useSelector((state) => state.countries.data);
  const currencies = useSelector((state) => state.currencies.data);
  const countriesLoad = useSelector(
    (state) => state.countries.load.getCountries
  );
  const currenciesLoad = useSelector(
    (state) => state.currencies.load.getCurrencies
  );
  const addDonorLoad = useSelector((state) => state.donors.load.addDonor);
  const editDonorLoad = useSelector((state) => state.donors.load.editDonor);
  const addNotification = useNotification();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(edit ? editDonorSchema : addDonorSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      if (edit) {
        const res = await dispatch(editDonor({ id, reqData: data }));
        if (editDonor.fulfilled.match(res)) {
          dispatch(closeModal());
          dispatch(setSelectedDonor(res.payload));
          addNotification("Donor updated successfully!", "success");
        } else {
          addNotification(res.payload.message, "error");
        }
      } else {
        const res = await dispatch(addDonor(data));
        if (addDonor.fulfilled.match(res)) {
          dispatch(closeModal());
          addNotification("Donor added successfully!", "success");
        } else {
          addNotification(res.payload.message, "error");
        }
      }
    } catch (error) {
      addNotification(error.message, "warning");
    }
  };

  useLayoutEffect(() => {
    dispatch(getCountries());
    dispatch(getCurrencies());
    // if (edit) {
    //   dispatch(getDonor(id));
    // }

    return () => {
      dispatch(unsetCountries());
      dispatch(unsetCurrencies());
    };
  }, []);

  useEffect(() => {
    if (donor.id) {
      reset({
        id: donor.id,
        province_id: donor.province_id,
        country_id: donor.country_id,
        name: donor.name,
        email: donor.email,
        address1: donor.address1,
        address2: donor.address2,
        phone: donor.phone,
        phone2: donor.phone2,
      });
    } else {
      if (user.id) {
        reset({ province_id: user.province_id });
      }
    }
  }, [donor, countries]);

  if (
    (donorLoad === loadStatus.loading && edit) ||
    countriesLoad === loadStatus.loading
  ) {
    return (
      <div className="min-w-72 sm:min-w-96 w-full h-96">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-w-72 sm:min-w-[30rem] w-full">
      <AddResource title={edit ? "Edit Donor" : "Add Donor"} />
      <form
        autoComplete="off"
        autoCorrect="off"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col justify-center items-center gap-2 w-full">
          <InputField
            register={register}
            type="text"
            autoComplete="off"
            name="name"
            label="Name"
            errors={errors.name}
          />
          <SelectField
            data={[{ id: "", name: "--Select Country" }, ...countries]}
            register={register}
            name="country_id"
            label="Country"
            errors={errors.country_id}
          />
          {!edit && (
            <SelectField
              register={register}
              name="currency_id"
              label="Account Currency"
              data={[{ id: "", name: "--Select Currency" }, ...currencies]}
              errors={errors.currency_id}
            />
          )}
          <InputField
            register={register}
            type="text"
            autoComplete="off"
            name="address1"
            label="Address 1"
            errors={errors.address1}
          />
          <InputField
            register={register}
            type="text"
            autoComplete="off"
            name="address2"
            label="Address 2"
            errors={errors.address2}
          />
          <PhoneInputField
            register={register}
            type="text"
            autoComplete="off"
            name="phone"
            label="Phone"
            errors={errors.phone}
            setValue={setValue}
            defaultValue={donor.phone || ""}
          />
          <PhoneInputField
            register={register}
            type="text"
            autoComplete="off"
            name="phone2"
            label="Mobile"
            errors={errors.phone2}
            setValue={setValue}
            defaultValue={donor.phone2 || ""}
          />
          <InputField
            register={register}
            type="text"
            autoComplete="off"
            name="email"
            label="Email"
            errors={errors.email}
          />

          <InputField
            register={register}
            type="hidden"
            autoComplete="off"
            name="province_id"
            defaultValue={user.province_id}
          />
        </div>
        <ResourseButtons loading={addDonorLoad || editDonorLoad} />
      </form>
    </div>
  );
};

export default AddDonor;
